import Uppy, { IndexedObject } from "@uppy/core";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import { DragDrop, StatusBar } from "@uppy/react";
import Tus from "@uppy/tus";
import { FC, memo } from "react";
import { FieldProps, useDataProvider, useLocale, useRecordContext } from "react-admin";
import FileField from "./FileField";
import { REACT_APP_FILES_URL, removeFiles } from "./fn";
import uppyFR from "./uppyFR";

interface UploaderProps<TMeta extends IndexedObject<any> = {}> extends FieldProps {
    source: string;
    override?: boolean;
    autoProceed?: boolean;
    allowMultipleUploads?: boolean;
    meta?: TMeta;
    maxFileSize?: number;
    minFileSize?: number;
    maxTotalFileSize?: number;
    maxNumberOfFiles?: number;
    minNumberOfFiles?: number;
    allowedFileTypes?: string[];
    disabled?: boolean;
};

const TusConfigDefault = {
    endpoint: REACT_APP_FILES_URL,
    removeFingerprintOnSuccess: true,
    retryDelays: [0, 1000, 3000, 5000]
};



const Uploader: FC<UploaderProps> = memo((props: UploaderProps) => {
    const dataProvider = useDataProvider();
    const record = useRecordContext();
    const { override, autoProceed, allowMultipleUploads, meta, maxTotalFileSize, minFileSize, maxFileSize, minNumberOfFiles, maxNumberOfFiles, allowedFileTypes, ...propsOthers } = props;
    const { source, resource, disabled } = propsOthers;
    const lang = useLocale();
    const locale = lang === "fr" ? uppyFR : undefined;
    const uppy = new Uppy({
        id: source,
        locale,
        autoProceed,
        meta,
        allowMultipleUploads,
        restrictions: {
            maxTotalFileSize, minFileSize, maxFileSize, minNumberOfFiles, maxNumberOfFiles, allowedFileTypes
        }
    })
        .use(Tus, { ...TusConfigDefault, id: `${source}Tus` })
        .on("complete", async (result) => {
            if (result?.successful?.length > 0) {
                const prev = record[source] || [];
                const ids = override ? [] : [...prev];
                for (const file of result.successful) {
                    if (override) {
                        await removeFiles(dataProvider, prev);
                    }
                    const { name, size, type, uploadURL, extension } = file;
                    const tusId = uploadURL.substring(REACT_APP_FILES_URL.length);
                    await dataProvider.create("file", {
                        data: {
                            name,
                            size,
                            type,
                            tusId,
                            extension
                        }
                    }).then(({ data }) => {
                        ids.push(data.id);
                    });
                    const id = record.id;
                    const data = {};
                    const previousData = record;
                    data[source] = maxNumberOfFiles === 1 ? ids[0] : "{" + ids + "}";
                    dataProvider.update(resource, {
                        id,
                        data,
                        previousData
                    });

                }

            }
            if (result?.failed?.length > 0) {
                console.error(result.failed);
            }
        });

    return <>
        {!disabled && <DragDrop uppy={uppy} />}
        <FileField {...propsOthers} />
        <StatusBar uppy={uppy} />
    </>;
});

Uploader.defaultProps = {
    addLabel: true,
    autoProceed: true,
    allowMultipleUploads: true,
    minFileSize: null,
    maxFileSize: 1e+8, // 100Mb
    maxTotalFileSize: null,
    minNumberOfFiles: null,
    maxNumberOfFiles: 1,
    allowedFileTypes: null,
    override: true,
    disabled: false
};

export default Uploader;